import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { calculateTotal } from '../lib/orderUtils';
import { buildOrderItems } from '../lib/orderItems';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IS_DEV = process.env.NODE_ENV === 'development';

const LoadingPanel = ({ title, subtitle, total }) => (
  <>
    <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
    <h2 className="text-2xl font-bold text-foreground mb-4">{title}</h2>
    <p className="text-lg text-muted-foreground mb-4">
      Totaal: {'\u20AC'}{total.toFixed(2)}
    </p>
    <p className="text-muted-foreground">{subtitle}</p>
  </>
);

const ErrorPanel = ({ error, onRetry }) => (
  <>
    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
      <AlertCircle className="w-12 h-12 text-red-500" />
    </div>
    <h2 className="text-2xl font-bold text-foreground mb-4">Betaling mislukt</h2>
    <p className="text-muted-foreground mb-6">{error}</p>
    <Button
      onClick={onRetry}
      className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 text-lg"
      data-testid="retry-payment-btn"
    >
      Opnieuw proberen
    </Button>
  </>
);

export const PaymentScreen = ({ order, setOrder }) => {
  const [status, setStatus] = useState('creating'); // creating, redirecting, error
  const [error, setError] = useState(null);
  const orderRef = useRef(order);
  const startedRef = useRef(false);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const total = calculateTotal(order);

  const ensureOrderInSupabase = useCallback(async (currentOrder, orderTotal) => {
    // If we already created an order in a previous attempt, reuse it.
    if (currentOrder.orderId) {
      // Make sure total + status are up-to-date (status reset to pending on retry).
      await supabase
        .from('orders')
        .update({ total_price: orderTotal, payment_status: 'pending' })
        .eq('id', currentOrder.orderId);
      return currentOrder.orderId;
    }

    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .insert({ total_price: orderTotal, payment_status: 'pending' })
      .select()
      .single();

    if (orderError || !orderData) {
      throw new Error('Kon bestelling niet aanmaken');
    }

    const orderId = orderData.id;

    // Insert main + burger + sides as separate rows
    const rows = buildOrderItems(orderId, currentOrder);
    if (rows.length) {
      const { error: itemsError } = await supabase.from('order_items').insert(rows);
      if (itemsError) {
        // Clean up the orphan order so retry creates a fresh one
        await supabase.from('orders').delete().eq('id', orderId);
        throw new Error('Kon bestelitems niet opslaan');
      }
    }

    setOrder(prev => ({ ...prev, orderId }));
    return orderId;
  }, [setOrder]);

  const createPaymentFlow = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;

    try {
      const currentOrder = orderRef.current;
      const orderTotal = calculateTotal(currentOrder);

      const orderId = await ensureOrderInSupabase(currentOrder, orderTotal);

      setStatus('redirecting');

      const response = await fetch(`${API_URL}/api/payments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          amount: orderTotal,
          description: `Pekika Point - ${currentOrder.product?.name || 'Bestelling'}`,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Betaling aanmaken mislukt');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (err) {
      if (IS_DEV) console.error('Payment flow error:', err);
      setError(err.message || 'Er ging iets mis');
      setStatus('error');
    }
  }, [ensureOrderInSupabase]);

  useEffect(() => {
    createPaymentFlow();
  }, [createPaymentFlow]);

  const handleRetry = () => {
    startedRef.current = false;
    setStatus('creating');
    setError(null);
    createPaymentFlow();
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <PageHeader title="Betaling" subtitle="Je wordt doorgestuurd naar de betaalpagina" />

      <div className="container max-w-2xl mx-auto px-6 mt-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-12 bg-card border-2 border-border text-center">
            {status === 'creating' && (
              <LoadingPanel title="Bestelling verwerken..." subtitle="Even geduld alstublieft" total={total} />
            )}
            {status === 'redirecting' && (
              <LoadingPanel title="Doorsturen naar betaling..." subtitle="Je wordt doorgestuurd naar Mollie" total={total} />
            )}
            {status === 'error' && (
              <ErrorPanel error={error} onRetry={handleRetry} />
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentScreen;
